import React, { useCallback } from 'react';
import { createPortal } from 'react-dom';
import spMarkUrl from '@/assets/images/logo/sp-mark-tile.png';
import { APP_STORE_URLS, type NativeAppPlatform } from '@/constants';
import { useLockScroll } from '@/hooks';
import { cn, openUrlInDefaultBrowser } from '@/utils';

interface ForceAppUpdateModalProps {
	platform: NativeAppPlatform;
}

// Figma 71152:92554 (iOS) / 71155:93402 (Android): only the button differs.
const BUTTON_BY_PLATFORM: Record<NativeAppPlatform, { label: string; radius: string }> = {
	ios: { label: 'Update on the App Store', radius: 'rounded-[24px]' },
	android: { label: 'Update on the Google Play', radius: 'rounded-[12px]' },
};

const TILE_GRADIENT =
	'radial-gradient(63.7px 63.7px at 40px 30.4px, #21478F 0%, #162E60 50%, #112148 75%, #0B1430 100%)';

const HEADLINE_ID = 'force-app-update-title';
const BODY_ID = 'force-app-update-body';

/**
 * Bottom sheet that locks an outdated WebToNative build until the user updates.
 *
 * Deliberately not a Radix Dialog: if another Radix modal opens after this one,
 * its DismissableLayer turns off pointer events on <body> for every older
 * layer, which would leave this sheet visible but dead. A plain portal with
 * its own `pointer-events: auto` stays interactive regardless of what mounts
 * later. There is no close affordance by design.
 */
export function ForceAppUpdateModal({
	platform,
}: ForceAppUpdateModalProps): React.ReactElement | null {
	useLockScroll(true);
	const button = BUTTON_BY_PLATFORM[platform];

	const handleUpdate = useCallback(() => {
		openUrlInDefaultBrowser(APP_STORE_URLS[platform]);
	}, [platform]);

	if (typeof document === 'undefined') return null;

	return createPortal(
		<div
			id="force-app-update"
			role="alertdialog"
			aria-modal="true"
			aria-labelledby={HEADLINE_ID}
			aria-describedby={BODY_ID}
			className="fixed inset-0 z-[2000] bg-[rgba(20,24,33,0.32)] font-['DM_Sans'] backdrop-blur-[8px]"
			style={{ pointerEvents: 'auto' }}
			onTouchMove={(event) => event.preventDefault()}
		>
			<div
				className="absolute inset-x-0 bottom-0 rounded-t-[20px] border border-[#DCE3F9] bg-white px-6 pt-6"
				style={{ paddingBottom: 'calc(16px + env(safe-area-inset-bottom))' }}
			>
				<div className="flex items-center gap-[14px]">
					<div
						className="flex size-20 shrink-0 items-center justify-center overflow-hidden rounded-[16px] shadow-[0_8px_24px_rgba(38,103,255,0.32)]"
						style={{ backgroundImage: TILE_GRADIENT }}
					>
						<img src={spMarkUrl} alt="" draggable={false} className="h-[38px] w-[50px]" />
					</div>

					<div className="flex min-w-0 flex-1 flex-col items-start gap-[6px]">
						<span className="inline-flex h-[26px] items-center rounded-[8px] border-[0.5px] border-[#DCE3F9] bg-[#EEF2FF] px-2 text-[12px] font-semibold leading-[18px] tracking-[0.96px] text-[#2667FF]">
							UPDATE REQUIRED
						</span>
						<h2
							id={HEADLINE_ID}
							className="m-0 text-[22px] font-bold leading-6 tracking-[-0.616px] text-[#0A0A0A]"
						>
							One quick update and you’re back in the game!
						</h2>
					</div>
				</div>

				<p
					id={BODY_ID}
					className="mt-5 text-[16px] font-normal leading-[22px] tracking-[-0.512px] text-[#474747]"
				>
					Updating keeps your forecasts and leaderboard positions exactly as you left them.
				</p>

				<button
					type="button"
					onClick={handleUpdate}
					className={cn(
						'mt-5 flex h-12 w-full items-center justify-center bg-[#2667FF] px-4 text-[16px] font-semibold leading-[18px] tracking-[-0.448px] text-white active:opacity-90',
						button.radius
					)}
				>
					{button.label}
				</button>
			</div>
		</div>,
		document.body
	);
}
